import { useEffect, useState } from "react";
import { useAuth } from "@/context/Auth";
import { supabase } from "@/integrations/supabase/client";
import { fetchAdminResponses } from "@/api/adminResponses";
import { isAdminEffective, adminGetReviewerRatingActivity, adminListReviewerRatings, adminListQuestionnaireItems } from "@/lib/admin";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useAuthRoles } from "@/hooks/useAuthRoles";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { Plus, MoreVertical, Eye, Users, Copy, Archive, AlertTriangle, CheckCircle } from "lucide-react";
import { isSelfAssessmentOnly } from "@/lib/questionnaireType";
import { formatDistanceToNow } from "date-fns";

type AssessmentRow = {
  id: string;
  questionnaire_id: string;
  status: string;
  created_at: string;
  submitted_at: string | null;
  updated_at: string;
  respondent_progress: number;
  reviewer_progress: number;
  overall_score: number | null;
  needs_attention: boolean;
  notes_count: number;
};

type Questionnaire = {
  id: string;
  category: string | null;
  meta: Record<string, unknown> | null;
};

interface Props {
  portfolioId: string;
  portfolioName?: string;
  category?: string | null;
}

// Threshold constants
const SCORE_THRESHOLDS = {
  STRONG: 80,
  ADEQUATE: 50,
};

export function PortfolioAssessmentsPanel({ portfolioId, portfolioName, category }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { roles } = useAuthRoles();
  
  // Get admin context from parent navigation state (if present)
  const adminContext = (location.state as { adminContext?: Record<string, string> } | null)?.adminContext;
  
  const [assessments, setAssessments] = useState<AssessmentRow[]>([]);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [questionnaireMap, setQuestionnaireMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [assessmentName, setAssessmentName] = useState("");
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<string>("");
  const [creating, setCreating] = useState(false);

  // Roles that can create assessments - admin, master_reviewer, reviewer have elevated permissions
  // po (portfolio owner) manages their portfolios
  const safeRoles = roles ?? [];
  const { role: effectiveRole } = useEffectiveRole();
  const { tier } = useAuth();
  const isTier1PM = effectiveRole === "portfolio_manager" && tier === "tier_1";
  const isAdmin = effectiveRole === "admin";
  const canCreateAssessment = safeRoles.includes("admin") || safeRoles.includes("master_reviewer") || safeRoles.includes("po");

  const getQuestionnaireTitle = (qId: string) => {
    const q = questionnaires.find((qq) => qq.id === qId);
    const meta = (q?.meta ?? {}) as Record<string, unknown>;
    const title = (meta.title ?? meta.name ?? meta.label) as string | undefined;
    return typeof title === "string" && title.trim().length > 0 ? title.trim() : null;
  };

  const getQuestionnaireDisplay = (qId: string) => {
    // Prefer portfolio_name as primary label, category as secondary
    if (portfolioName) return portfolioName;
    const title = getQuestionnaireTitle(qId);
    return title ?? "Questionnaire";
  };

  const fetchAssessments = async () => {
    let rpcData: any[] | null = null;
    let error: any = null;

    if (isAdmin) {
      const res = await (supabase.rpc as any)("admin_list_assessments_for_org", {
        p_org_id: portfolioId,
      });
      rpcData = res.data;
      error = res.error;
    } else {
      const res = await (supabase.rpc as any)("pm_list_assessments_for_portfolio", {
        p_portfolio_id: portfolioId,
      });
      rpcData = res.data;
      error = res.error;
    }

    if (error) {
      console.error("Error fetching assessments:", error);
      toast({ title: "Error", description: "Failed to load assessments", variant: "destructive" });
      return;
    }

    // Map RPC response to expected shape
    const baseData = (rpcData || []) as Array<{
      id: string;
      org_id: string;
      questionnaire_id: string;
      status: string;
      created_at: string;
      submitted_at: string | null;
    }>;

    if (!baseData || baseData.length === 0) {
      setAssessments([]);
      return;
    }

    // Enrich each assessment with progress and scores
    const enrichedAssessments: AssessmentRow[] = await Promise.all(
      baseData.map(async (a) => {
        // Get question count for this questionnaire - use admin RPC if admin
        const isAdmin = await isAdminEffective();
        let totalQuestions = 0;
        
        if (isAdmin) {
          console.log("[ADMIN] Using RPC for questionnaire_items count");
          const itemsData = await adminListQuestionnaireItems(a.questionnaire_id);
          totalQuestions = itemsData.length;
        } else {
          const { data: qItems } = await (supabase.rpc as any)(
            "pm_get_questionnaire_items",
            { p_portfolio_id: portfolioId, p_questionnaire_id: a.questionnaire_id }
          );
          totalQuestions = (qItems ?? []).length;
        }

        // Get responses - use admin RPC if admin, PM RPC otherwise
        let responseRows: any[] = [];
        if (isAdmin) {
          responseRows = await fetchAdminResponses(a.id, null);
        } else {
          const { data: pmResp } = await (supabase.rpc as any)("pm_get_responses", { p_portfolio_id: portfolioId, p_assessment_id: a.id });
          responseRows = pmResp ?? [];
        }
        const answeredResponses = responseRows.filter((r: any) => r.score !== null && r.score !== undefined);
        const answeredCount = answeredResponses.length;
        const latestResponseAt = responseRows.reduce((max: string | null, r: any) => {
          if (!r.updated_at) return max;
          if (!max) return r.updated_at;
          return new Date(r.updated_at) > new Date(max) ? r.updated_at : max;
        }, null as string | null);

        // Reviewer ratings - reuse isAdmin from above
        let reviewedCount = 0;
        let notesCount = 0;
        let latestRatingAt: string | null = null;

        if (isAdmin) {
          console.log("[ADMIN] Using admin RPCs for reviewer ratings");
          // Use admin RPC for activity timestamps
          const activityData = await adminGetReviewerRatingActivity(a.id);
          if (activityData) {
            latestRatingAt = activityData.latest_scored_at || activityData.latest_notes_at || null;
          }
          // Use admin_list_reviewer_ratings for counts
          const ratingsData = await adminListReviewerRatings(a.id);
          reviewedCount = ratingsData.filter((r: any) => r.reviewer_score !== null).length;
          notesCount = ratingsData.filter((r: any) => r.reviewer_notes && r.reviewer_notes !== '').length;
        } else {
          // Non-admin: use PM RPC for reviewer ratings
          const { data: pmRatings, error: pmRatingsErr } = await (supabase.rpc as any)(
            "pm_get_reviewer_ratings",
            { p_assessment_id: a.id }
          );
          if (pmRatingsErr) console.warn("[PM] pm_get_reviewer_ratings error", pmRatingsErr);

          const ratingsArr = pmRatings ?? [];
          reviewedCount = ratingsArr.filter((r: any) => r.reviewer_score !== null).length;
          notesCount = ratingsArr.filter((r: any) => r.reviewer_notes && r.reviewer_notes !== '').length;

          // Get latest rating timestamp
          latestRatingAt = ratingsArr.reduce((max: string | null, r: any) => {
            if (!r.updated_at) return max;
            if (!max) return r.updated_at;
            return new Date(r.updated_at) > new Date(max) ? r.updated_at : max;
          }, null as string | null);
        }

        // Calculate overall score from admin responses
        let overallScore: number | null = null;
        if (answeredResponses.length > 0) {
          const sum = answeredResponses.reduce((acc, r) => acc + (r.score || 0), 0);
          // Convert from 0-4 scale to 0-100
          overallScore = Math.round((sum / answeredResponses.length / 4) * 100);
        }

        const total = totalQuestions || 1;
        const respondentProgress = Math.min(100, Math.round(((answeredCount || 0) / total) * 100));
        const reviewerProgress = Math.min(100, Math.round(((reviewedCount || 0) / total) * 100));

        // Determine last updated timestamp
        const timestamps = [
          a.created_at,
          latestResponseAt,
          latestRatingAt,
        ].filter(Boolean) as string[];
        const latestUpdate = timestamps.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || a.created_at;

        // Needs attention logic — skip reviewer-based triggers for self-assessment-only questionnaires
        const selfOnly = isSelfAssessmentOnly(a.questionnaire_id);
        const needsAttention = selfOnly
          ? (overallScore !== null && overallScore < SCORE_THRESHOLDS.ADEQUATE)
          : (reviewerProgress < 20 && respondentProgress > 80) ||
            (overallScore !== null && overallScore < SCORE_THRESHOLDS.ADEQUATE) ||
            ((notesCount || 0) > 0 && reviewerProgress < 50);

        return {
          id: a.id,
          questionnaire_id: a.questionnaire_id,
          status: a.status,
          created_at: a.created_at,
          submitted_at: a.submitted_at,
          updated_at: latestUpdate,
          respondent_progress: respondentProgress,
          reviewer_progress: reviewerProgress,
          overall_score: overallScore,
          needs_attention: needsAttention,
          notes_count: notesCount || 0,
        };
      })
    );

    setAssessments(enrichedAssessments);
  };

  const fetchQuestionnaires = async () => {
    // Use PM-scoped RPC to avoid raw table access
    const { data, error } = await (supabase.rpc as any)("pm_list_questionnaires", {
      p_portfolio_id: portfolioId,
    });

    if (error) {
      console.error("Error fetching questionnaires via pm_list_questionnaires:", error);
      return;
    }

    const qList: Questionnaire[] = (data || []).map((q: any) => ({
      id: q.id,
      category: q.category,
      meta: q.meta,
    }));
    setQuestionnaires(qList);
    const map: Record<string, string> = {};
    qList.forEach((q) => {
      const meta = (q.meta ?? {}) as Record<string, unknown>;
      const title = (meta.title ?? meta.name ?? meta.label) as string | undefined;
      map[q.id] = typeof title === "string" && title.trim().length > 0 ? title.trim() : "";
    });
    setQuestionnaireMap(map);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchAssessments(), fetchQuestionnaires()]);
      setLoading(false);
    })();
  }, [portfolioId]);

  const handleCreateAssessment = async () => {
    if (!selectedQuestionnaire) {
      toast({ title: "Error", description: "Please select a questionnaire", variant: "destructive" });
      return;
    }
    if (!assessmentName.trim()) {
      toast({ title: "Error", description: "Please enter an assessment name", variant: "destructive" });
      return;
    }

    setCreating(true);
    const { data, error } = await supabase
      .from("assessments")
      .insert({
        org_id: portfolioId,
        questionnaire_id: selectedQuestionnaire,
        status: "Draft",
      })
      .select()
      .single();

    setCreating(false);

    if (error) {
      console.error("Error creating assessment:", error);
      toast({ title: "Error", description: "Failed to create assessment", variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Assessment created" });
      setShowCreateModal(false);
      setSelectedQuestionnaire("");
      setAssessmentName("");
      await fetchAssessments();
      if (data) {
        navigate(`/assessment/${data.id}`);
      }
    }
  };

  const handleRowClick = (e: React.MouseEvent, assessmentId: string, assessmentName: string) => {
    // Prevent parent row/container clicks from interfering
    e.preventDefault();
    e.stopPropagation();
    // Pass returnTo so the Back button knows where to go
    const returnTo = encodeURIComponent(`/org-admin/${portfolioId}/assessments`);
    console.log("[PAP] row click", { assessmentId, navigatingTo: `/assessments/${assessmentId}/overview?returnTo=${returnTo}` });
    
    // If we have admin context, pass it through to the assessment overview
    if (adminContext) {
      navigate(`/assessments/${assessmentId}/overview?returnTo=${returnTo}`, {
        state: {
          adminContext: {
            ...adminContext,
            assessmentId,
            assessmentName,
          }
        }
      });
    } else {
      navigate(`/assessments/${assessmentId}/overview?returnTo=${returnTo}`);
    }
  };

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "outline" | "destructive" => {
    switch (status) {
      case "Complete":
      case "Completed":
        return "default";
      case "Submitted":
      case "In Review":
        return "secondary";
      case "In Progress":
        return "outline";
      case "Draft":
      default:
        return "outline";
    }
  };

  const getHealthBadge = (needsAttention: boolean) => {
    if (needsAttention) {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertTriangle className="h-3 w-3" />
          Attention
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="gap-1 text-green-600 border-green-600">
        <CheckCircle className="h-3 w-3" />
        OK
      </Badge>
    );
  };

  const formatLastUpdated = (dateStr: string) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return "—";
    }
  };

  // Debug log for render
  console.log("[PortfolioAssessmentsPanel] debug", { 
    assessmentsCount: assessments.length,
    loading,
    portfolioId
  });

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Assessments</h3>
        {canCreateAssessment && (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Assessment
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : assessments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No assessments found.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Assessment Name</TableHead>
              <TableHead>Questionnaire / Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Respondent Progress</TableHead>
              {!isTier1PM && <TableHead className="text-center">Reviewer Progress</TableHead>}
              <TableHead className="text-center">Health</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assessments.map((a) => (
              <TableRow 
                key={a.id} 
                className="cursor-pointer hover:bg-muted/50"
                onClick={(e) => handleRowClick(e, a.id, getQuestionnaireDisplay(a.questionnaire_id))}
              >
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {getQuestionnaireDisplay(a.questionnaire_id)}
                    {isSelfAssessmentOnly(a.questionnaire_id) && (
                      <Badge variant="outline" className="text-xs border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400">
                        Self-Assessment Only
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {category || questionnaires.find(q => q.id === a.questionnaire_id)?.category || "—"}
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusBadgeVariant(a.status)}>
                    {a.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <span className={a.respondent_progress >= 80 ? "text-green-600 font-medium" : ""}>
                    {a.respondent_progress}%
                  </span>
                </TableCell>
                {!isTier1PM && (
                  <TableCell className="text-center">
                    {isSelfAssessmentOnly(a.questionnaire_id) ? (
                      <span className="text-xs text-muted-foreground">Self-Assessment Only</span>
                    ) : (
                      <span className={a.reviewer_progress >= 80 ? "text-green-600 font-medium" : ""}>
                        {a.reviewer_progress}%
                      </span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-center">
                  {getHealthBadge(a.needs_attention)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatLastUpdated(a.updated_at)}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/assessment/${a.id}`);
                      }}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Overview
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/assessment/${a.id}?tab=users`);
                      }}>
                        <Users className="h-4 w-4 mr-2" />
                        Users
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => e.stopPropagation()}
                        disabled
                        className="text-muted-foreground"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate Assessment
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => e.stopPropagation()}
                        disabled
                        className="text-muted-foreground"
                      >
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create Assessment Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Assessment</DialogTitle>
            <DialogDescription>
              Create a new assessment for this portfolio company.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Assessment Name</label>
              <Input
                placeholder="e.g., Q4 2024 Security Assessment"
                value={assessmentName}
                onChange={(e) => setAssessmentName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Questionnaire</label>
              <Select value={selectedQuestionnaire} onValueChange={setSelectedQuestionnaire}>
                <SelectTrigger>
                  <SelectValue placeholder="Select questionnaire" />
                </SelectTrigger>
                <SelectContent>
                  {questionnaires.map((q) => (
                    <SelectItem key={q.id} value={q.id}>
                      {getQuestionnaireDisplay(q.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAssessment} 
              disabled={creating || !selectedQuestionnaire || !assessmentName.trim()}
            >
              {creating ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}